import { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { ApiError } from "@/lib/api/client"
import { publicConsumerApi } from "@/lib/api/public-consumer"
import type { ApiEstimate } from "@/lib/api/types"
import { ConsumerFlow } from "@/components/estimates/consumer-intake-estimate/consumer-flow"
import { resolveDevice } from "@/components/estimates/consumer-intake-estimate/device-mode"
import {
  ShopGateDead,
  ShopGateDisabled,
  ShopGateExpired,
  ShopGateLoading,
  ShopGateRetry,
  usePublicShop,
} from "@/components/estimates/consumer-intake-estimate/use-public-shop"
import type { IntakeSurface } from "@/components/estimates/consumer-intake-estimate/intake-flow-types"

// Public consumer entry — /go/:shopId. No login, no app shell. Resolves the
// shop (gates: loading / dead link / intake switched off), the arrival
// surface (?src=qr|sms, else link — embed/widget/kiosk have their own routes),
// the device (?device= override beats detection) and an optional desktop →
// phone continuation (?c=<token>), then hands off to the flow. MOCK-ONLY
// endpoints (see public-consumer.ts).

type Continuation =
  | { status: "none" }
  | { status: "loading" }
  | { status: "ready"; estimate: ApiEstimate }
  | { status: "expired"; trackable: boolean }
  // Network / server trouble — the link may be fine; offer a retry, never a
  // "start over" that would create a second lead.
  | { status: "error" }

function surfaceFrom(src: string | null): IntakeSurface {
  return src === "qr" || src === "sms" ? src : "link"
}

function shopIdOf(estimate: ApiEstimate): number | null {
  return estimate.dealership?.id ?? estimate.bodyshop?.id ?? null
}

export default function ConsumerFlowPage() {
  const { shopId } = useParams<{ shopId: string }>()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const { shop, status } = usePublicShop(shopId)

  const token = search.get("c")
  const surface = surfaceFrom(search.get("src"))
  // Resolved once per visit — a resize must never flip a phone into the
  // desktop handoff mid-walk.
  const [device] = useState(() => resolveDevice(new URLSearchParams(window.location.search)))
  const [continuation, setContinuation] = useState<Continuation>(
    token ? { status: "loading" } : { status: "none" },
  )
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!token) {
      setContinuation({ status: "none" })
      return
    }
    let alive = true
    setContinuation({ status: "loading" })
    // Only a phone "continues" (the mock stamps continuedAt + device=phone,
    // which is what the desktop mirror listens for). A desktop opening the
    // same link just reads the lead.
    const load =
      device === "phone"
        ? publicConsumerApi.continueIntake(token)
        : publicConsumerApi.getEstimate(token)
    load
      .then((estimate) => {
        if (!alive) return
        if (shopId && String(shopIdOf(estimate)) !== String(shopId)) {
          setContinuation({ status: "expired", trackable: false })
          return
        }
        setContinuation({ status: "ready", estimate })
      })
      .catch((err) => {
        if (!alive) return
        const status = err instanceof ApiError ? err.status : 0
        if (status === 410) setContinuation({ status: "expired", trackable: true })
        else if (status === 404) setContinuation({ status: "expired", trackable: false })
        else setContinuation({ status: "error" })
      })
    return () => {
      alive = false
    }
  }, [token, device, shopId, attempt])

  if (status === "loading" || continuation.status === "loading") return <ShopGateLoading />
  if (status === "dead" || !shop) return <ShopGateDead />
  if (!shop.intake.enabled) return <ShopGateDisabled shop={shop} />

  if (continuation.status === "error") {
    return <ShopGateRetry onRetry={() => setAttempt((a) => a + 1)} />
  }

  if (continuation.status === "expired") {
    const startNew = () => {
      const next = new URLSearchParams(search)
      next.delete("c")
      const query = next.toString()
      navigate(`/go/${shop.id}${query ? `?${query}` : ""}`, { replace: true })
    }
    return (
      <ShopGateExpired
        onStartNew={startNew}
        onTrack={continuation.trackable && token ? () => navigate(`/track/${token}`) : null}
      />
    )
  }

  return (
    <ConsumerFlow
      key={token ?? "fresh"}
      shop={shop}
      surface={surface}
      device={device}
      entry={
        continuation.status === "ready"
          ? { kind: "continuation", estimate: continuation.estimate }
          : { kind: "fresh" }
      }
      continuationToken={token ?? undefined}
    />
  )
}
